/**
 * Table de comptes de TVA paramétrable (par entreprise), lue par la déclaration de TVA.
 *
 * Rôles : collectée, déductible sur biens et services, déductible sur immobilisations, TVA due (à payer,
 * solde de liquidation) et crédit de TVA (à reporter). Les comptes « due » et « crédit » sont des comptes
 * de RÉSULTAT de la liquidation : la déclaration les ignore pour ne pas fausser collectée / déductible.
 *
 * Un motif est un compte exact (`4431`) ou un préfixe suivi de `*` (`4431*` = 4431 et ses sous-comptes).
 * Si plusieurs motifs conviennent, le plus précis l'emporte (le plus long ; à longueur égale, l'exact).
 *
 * Valeurs par défaut = comptes du code (4431 / 4452 / 4451) + alias historiques, plus 4441 (TVA due du PRD §7.4)
 * et 4449 (crédit de TVA) rangés en « due » et « crédit » : un 4441 n'est donc plus lu comme TVA récupérable.
 * À valider par l'expert-comptable (DEC-06).
 */

export const ROLES_COMPTE_TVA = [
  'collectee',
  'deductible_biens',
  'deductible_immo',
  'due',
  'credit',
] as const;

export type RoleCompteTva = (typeof ROLES_COMPTE_TVA)[number];
export type TableComptesTva = Readonly<Record<RoleCompteTva, readonly string[]>>;
export type SurchargesComptesTva = Partial<Record<RoleCompteTva, readonly string[]>>;

export const COMPTES_TVA_PAR_DEFAUT: TableComptesTva = Object.freeze({
  collectee: Object.freeze(['442', '4431*', '4457*']),
  deductible_biens: Object.freeze(['444', '4452*', '4456*']),
  deductible_immo: Object.freeze(['4451*']),
  due: Object.freeze(['443', '4441*']),
  credit: Object.freeze(['4449*']),
});

export class ComptesTvaInvalideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ComptesTvaInvalideError';
  }
}

const FORMAT_MOTIF = /^[1-9]\d{2,7}\*?$/;

export function validerTableComptesTva(table: TableComptesTva): void {
  const vus = new Map<string, RoleCompteTva>();
  for (const role of ROLES_COMPTE_TVA) {
    const motifs = table[role];
    if (!Array.isArray(motifs) || motifs.length === 0) {
      throw new ComptesTvaInvalideError(`Aucun compte pour le rôle de TVA « ${role} ».`);
    }
    for (const m of motifs) {
      if (typeof m !== 'string' || !FORMAT_MOTIF.test(m)) {
        throw new ComptesTvaInvalideError(`Compte de TVA invalide (${role}) : « ${String(m)} » (ex. 4431 ou 4431*).`);
      }
      if (!m.startsWith('44')) {
        throw new ComptesTvaInvalideError(`Compte de TVA « ${m} » refusé (${role}) : un compte d'État 44… est attendu.`);
      }
      const autre = vus.get(m);
      if (autre && autre !== role) {
        throw new ComptesTvaInvalideError(`Le compte ${m} est affecté à deux rôles de TVA (${autre} et ${role}).`);
      }
      vus.set(m, role);
    }
  }
}

export function resoudreComptesTva(surcharges?: SurchargesComptesTva | null): TableComptesTva {
  const table: Record<RoleCompteTva, readonly string[]> = { ...COMPTES_TVA_PAR_DEFAUT };
  if (surcharges) {
    for (const [role, motifs] of Object.entries(surcharges)) {
      if (!(ROLES_COMPTE_TVA as readonly string[]).includes(role)) {
        throw new ComptesTvaInvalideError(`Rôle de TVA inconnu : ${role}`);
      }
      if (motifs !== undefined) table[role as RoleCompteTva] = motifs.map((m) => String(m).trim());
    }
  }
  validerTableComptesTva(table);
  return table;
}

/** Rôle de TVA d'un compte (motif le plus précis), ou null si ce n'est pas un compte de TVA. */
export function roleCompteTva(
  compte: string,
  table: TableComptesTva = COMPTES_TVA_PAR_DEFAUT,
): RoleCompteTva | null {
  const c = compte.trim();
  let meilleur: { role: RoleCompteTva; longueur: number; exact: boolean } | null = null;
  for (const role of ROLES_COMPTE_TVA) {
    for (const motif of table[role]) {
      const prefixe = motif.endsWith('*');
      const base = prefixe ? motif.slice(0, -1) : motif;
      const ok = prefixe ? c.startsWith(base) : c === base;
      if (!ok) continue;
      const cand = { role, longueur: base.length, exact: !prefixe };
      if (
        !meilleur ||
        cand.longueur > meilleur.longueur ||
        (cand.longueur === meilleur.longueur && cand.exact && !meilleur.exact)
      ) {
        meilleur = cand;
      }
    }
  }
  return meilleur?.role ?? null;
}
